import { Github, Linkedin, Gitlab } from "lucide-react";
import PyroLogo from "./icons/PyroStroke";
import CodebergLogo from "./icons/Codeberg";
import XLogo from "./icons/X";
import "../border.css";
import { Highlighter } from "./magic/Highlighter";

interface Social {
  name: string;
  url: string;
  color: string;
}

interface CurrentProject {
  text: string;
  name: string;
  url: string;
}

interface ProfileCardProps {
  name: string;
  title: string;
  bio: string;
  avatar: string;
  socials: Social[];
  currentProject?: CurrentProject;
}

export default function ProfileCard({
  name,
  title,
  bio,
  avatar,
  socials,
  currentProject,
}: ProfileCardProps) {
  return (
    <div className="flex justify-center items-center w-full md:w-4/5 mx-auto mt-10">
      <div className="w-full md:w-full mb-10 [background:linear-gradient(45deg,#0a0a0a,theme(colors.black)_50%,#0a0a0a)_padding-box,conic-gradient(from_var(--border-angle),theme(colors.slate.600/.48)_80%,_theme(colors.turquoise.DEFAULT)_86%,_theme(colors.turquoise.light)_90%,_theme(colors.turquoise.DEFAULT)_94%,_theme(colors.slate.600/.48))_border-box] border border-transparent animate-border rounded-xl p-6">
        <div className="flex flex-row space-x-4 mb-4">
          {socials.map((social) => (
            <a
              key={social.name}
              href={social.url}
              target="_blank"
              rel="noreferrer"
              className="text-white transition-colors hover:text-[var(--hover-color)]"
              style={{ '--hover-color': social.color }}
            >
              {
                social.name === "github" ? (
                  <Github size={24} />
                ) : social.name === "pyro" ? (
                  <PyroLogo size={24} />
                ) : social.name === "twitter" ? (
                  <XLogo size={24} />
                ) : social.name === "linkedin" ? (
                  <Linkedin size={24} />
                ) : social.name === "gitlab" ? (
                  <Gitlab size={24} />
                ) : social.name === "codeberg" ? (
                  <CodebergLogo size={24} />
                ) :
                  (
                    <div className="w-6 h-6 bg-turquoise rounded-full flex items-center justify-center text-black font-bold text-xs">
                      {social.name.charAt(0).toUpperCase()}
                    </div>
                  )
              }
            </a>
          ))}
        </div>

        <a
          href={socials.find((s) => s.name === "github")?.url || "#"}
          target="_blank"
          rel="noreferrer"
        >
          <img
            src={avatar || "/placeholder.svg?height=128&width=128"}
            alt={`${name}'s Profile`}
            className="rounded-full w-32 h-32 mb-4 mx-auto object-cover"
          />
        </a>

        <div className="text-center space-y-2">
          <p>
            <Highlighter action="underline" color="#ffedd9">
              <h1 className="text-2xl font-bold text-white">{name}</h1>
            </Highlighter>
          </p>
          <p className="text-turquoise">{title}</p>
          <div className="text-gray-300 mt-4">
            <p>{bio}</p>
            {currentProject && (
              <p className="mt-2">
                {currentProject.text}{" "}
                <a
                  href={currentProject.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-turquoise hover:text-turquoise-light transition-colors"
                >
                  {currentProject.name}
                </a>{" "}
                repo.
              </p>
            )}
          </div>
        </div>
      </div>
    </div >
  );
}
